import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { InputSwitch } from 'primereact/inputswitch';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import BusinessActions from '../../Redux/actions/business-actions';
import BusinessCustomersActions from '../../Redux/transition/customers/business-customer.reducer';
import CustomToast from '../../Components/CustomToast';
import { SignupReqExclusionsSection } from './SignupReqExclusionsSection';
import { TAG_GROUP_DROPDOWN_PAGE_OPTIONS } from '../Customers/Segments/tagUtils';

const normalizeTagGroups = (data) => {
    if (!data) return [];
    if (Array.isArray(data.groups)) return data.groups;
    if (Array.isArray(data)) return data;
    return [];
};

const normalizeSignupReqs = (data) => {
    if (!Array.isArray(data)) return [];
    return data
        .map((req) => ({
            id: req?.id,
            tagGroupId: req?.tagGroup?.id,
            name: req?.tagGroup?.name,
        }))
        .filter((req) => req.name);
};

export const SignupRequirementsPreference = () => {
    const { t } = useTranslation('common');
    const dispatch = useDispatch();
    const toggleIntentRef = useRef(null);
    const saveSubmittedRef = useRef(false);
    const saveIntentRef = useRef(null);
    const deleteSubmittedRef = useRef(false);

    const {
        business,
        signupReqs,
        fetchingSignupReqs,
        togglingReqsEnabled,
        errorTogglingReqsEnabled,
        creatingSignupReqs,
        errorCreateSignupReqs,
        deletingSignupReq,
        errorDeleteSignupReq,
    } = useSelector((state) => state.businesses);
    const { tagGroups, fetchingTagGroups } = useSelector((state) => state.businessCustomers);

    const businessId = business?.id;

    const [reqsEnabled, setReqsEnabled] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [showTagGroupDialog, setShowTagGroupDialog] = useState(false);
    const [selectedTagGroupIds, setSelectedTagGroupIds] = useState([]);
    const [awaitingTagGroupSelection, setAwaitingTagGroupSelection] = useState(false);
    const [saveAttempted, setSaveAttempted] = useState(false);
    const [reqToRemove, setReqToRemove] = useState(null);
    const [toastData, setToastData] = useState({ summary: '', severity: '', detail: '', life: 0 });
    const [toastTrigger, setToastTrigger] = useState(false);

    const getBusiness = useCallback((id) => dispatch(BusinessActions.businessRequest(id)), [dispatch]);
    const toggleBusinessReqsEnabled = useCallback(
        (id) => dispatch(BusinessActions.toggleReqsEnabledRequest(id)),
        [dispatch]
    );
    const getTagGroups = useCallback(
        (id) =>
            dispatch(
                BusinessCustomersActions.tagGroupRequest(id, TAG_GROUP_DROPDOWN_PAGE_OPTIONS)
            ),
        [dispatch]
    );
    const saveSignupReqs = useCallback(
        (id, tagGroupIds) => dispatch(BusinessActions.createSignupReqsRequest(id, tagGroupIds)),
        [dispatch]
    );
    const fetchSignupReqs = useCallback(
        (id) => dispatch(BusinessActions.fetchSignupReqsRequest(id)),
        [dispatch]
    );
    const fetchSignupReqExclusions = useCallback(
        (id) => dispatch(BusinessActions.fetchSignupReqExclusionsRequest(id)),
        [dispatch]
    );
    const removeSignupReq = useCallback(
        (id, signupReqId) => dispatch(BusinessActions.deleteSignupReqRequest(id, signupReqId)),
        [dispatch]
    );

    const tagGroupOptions = useMemo(() => normalizeTagGroups(tagGroups), [tagGroups]);
    const signupRequirementNames = useMemo(() => normalizeSignupReqs(signupReqs), [signupReqs]);
    const selectedRequirementTagGroupIds = useMemo(
        () => new Set(signupRequirementNames.map((req) => String(req.tagGroupId))),
        [signupRequirementNames]
    );
    const availableTagGroups = useMemo(() => {
        if (awaitingTagGroupSelection) return tagGroupOptions;
        return tagGroupOptions.filter((group) => !selectedRequirementTagGroupIds.has(String(group.id)));
    }, [awaitingTagGroupSelection, tagGroupOptions, selectedRequirementTagGroupIds]);

    const showToast = useCallback((severity, detail) => {
        setToastData({
            summary: severity === 'success' ? t('success') : t('err'),
            severity,
            detail,
            life: 3000,
        });
        setToastTrigger(true);
    }, [t]);

    useEffect(() => {
        if (!business) return;
        setReqsEnabled(business.reqsEnabled ?? false);
    }, [business]);

    useEffect(() => {
        if (!reqsEnabled || !businessId) return;
        fetchSignupReqs(businessId);
        fetchSignupReqExclusions(businessId);
    }, [reqsEnabled, businessId, fetchSignupReqs, fetchSignupReqExclusions]);

    useEffect(() => {
        if (togglingReqsEnabled) return;

        const intent = toggleIntentRef.current;
        if (!intent) return;

        if (errorTogglingReqsEnabled) {
            showToast('error', t('signupRequirements.error_info'));
            toggleIntentRef.current = null;
            setShowConfirm(false);
            setShowTagGroupDialog(false);
            setAwaitingTagGroupSelection(false);
            return;
        }

        if (intent === 'enable' && business?.reqsEnabled) {
            toggleIntentRef.current = null;
            setShowConfirm(false);
            setSelectedTagGroupIds([]);
            setSaveAttempted(false);
            setAwaitingTagGroupSelection(true);
            getTagGroups(businessId);
            setShowTagGroupDialog(true);
            return;
        }

        if (intent === 'disable') {
            toggleIntentRef.current = null;
            setShowConfirm(false);
            setReqsEnabled(false);
            showToast('success', t('signupRequirements.disable_success'));
            getBusiness(businessId);
            return;
        }

        if (intent === 'revert') {
            toggleIntentRef.current = null;
            setShowTagGroupDialog(false);
            setAwaitingTagGroupSelection(false);
            setSelectedTagGroupIds([]);
            setSaveAttempted(false);
            setReqsEnabled(false);
            getBusiness(businessId);
        }
    }, [
        togglingReqsEnabled,
        errorTogglingReqsEnabled,
        business?.reqsEnabled,
        businessId,
        getBusiness,
        getTagGroups,
        showToast,
        t,
    ]);

    useEffect(() => {
        if (creatingSignupReqs) return;
        if (!saveSubmittedRef.current) return;

        if (errorCreateSignupReqs) {
            saveSubmittedRef.current = false;
            saveIntentRef.current = null;
            showToast('error', t('signupRequirements.save_error'));
            return;
        }

        const intent = saveIntentRef.current;
        saveSubmittedRef.current = false;
        saveIntentRef.current = null;
        setAwaitingTagGroupSelection(false);
        setShowTagGroupDialog(false);
        setSaveAttempted(false);
        showToast(
            'success',
            intent === 'add'
                ? t('signupRequirements.tag_groups_save_success')
                : t('signupRequirements.enable_success')
        );
        fetchSignupReqs(businessId);
        getBusiness(businessId);
    }, [
        creatingSignupReqs,
        errorCreateSignupReqs,
        businessId,
        fetchSignupReqs,
        getBusiness,
        showToast,
        t,
    ]);

    useEffect(() => {
        if (deletingSignupReq) return;
        if (!deleteSubmittedRef.current) return;

        if (errorDeleteSignupReq) {
            deleteSubmittedRef.current = false;
            showToast('error', t('signupRequirements.tag_groups_delete_error'));
            return;
        }

        deleteSubmittedRef.current = false;
        setReqToRemove(null);
        showToast('success', t('signupRequirements.tag_groups_delete_success'));
    }, [
        deletingSignupReq,
        errorDeleteSignupReq,
        showToast,
        t,
    ]);

    const handleConfirmToggle = () => {
        if (reqsEnabled) {
            toggleIntentRef.current = 'disable';
            toggleBusinessReqsEnabled(businessId);
            return;
        }

        toggleIntentRef.current = 'enable';
        toggleBusinessReqsEnabled(businessId);
    };

    const handleOpenAddTagGroups = () => {
        setAwaitingTagGroupSelection(false);
        setSelectedTagGroupIds([]);
        setSaveAttempted(false);
        getTagGroups(businessId);
        setShowTagGroupDialog(true);
    };

    const handleTagGroupDialogClose = () => {
        if (creatingSignupReqs || togglingReqsEnabled) return;

        if (!awaitingTagGroupSelection) {
            setShowTagGroupDialog(false);
            setSelectedTagGroupIds([]);
            setSaveAttempted(false);
            return;
        }

        toggleIntentRef.current = 'revert';
        toggleBusinessReqsEnabled(businessId);
    };

    const handleTagGroupToggle = (tagGroupId, checked) => {
        setSelectedTagGroupIds((current) => {
            if (checked) {
                return current.some((groupId) => String(groupId) === String(tagGroupId))
                    ? current
                    : [...current, tagGroupId];
            }
            return current.filter((groupId) => String(groupId) !== String(tagGroupId));
        });
    };

    const handleSaveTagGroups = () => {
        setSaveAttempted(true);
        if (!selectedTagGroupIds.length) return;
        saveIntentRef.current = awaitingTagGroupSelection ? 'enable' : 'add';
        saveSubmittedRef.current = true;
        saveSignupReqs(businessId, selectedTagGroupIds);
    };

    const handleRequestRemoveTagGroup = (req) => {
        if (signupRequirementNames.length <= 1) {
            showToast('error', t('signupRequirements.tag_groups_last_required'));
            return;
        }
        setReqToRemove(req);
    };

    const handleConfirmRemoveTagGroup = () => {
        if (!reqToRemove?.id) return;
        if (signupRequirementNames.length <= 1) {
            showToast('error', t('signupRequirements.tag_groups_last_required'));
            setReqToRemove(null);
            return;
        }
        deleteSubmittedRef.current = true;
        removeSignupReq(businessId, reqToRemove.id);
    };

    const confirmFooter = () => (
        <div className="flex justify-content-end">
            <Button
                label={t('common.no')}
                icon="pi pi-times"
                className="p-button-text"
                onClick={() => {
                    setShowConfirm(false);
                    setReqsEnabled(business?.reqsEnabled ?? false);
                }}
            />
            <Button
                label={t('common.ok')}
                icon="pi pi-check"
                loading={togglingReqsEnabled}
                onClick={handleConfirmToggle}
            />
        </div>
    );

    const tagGroupDialogFooter = () => (
        <div className="flex justify-content-end">
            <Button
                label={t('common.no')}
                icon="pi pi-times"
                className="p-button-text"
                onClick={handleTagGroupDialogClose}
                disabled={creatingSignupReqs || togglingReqsEnabled}
            />
            <Button
                label={t('signupRequirements.save')}
                icon="pi pi-check"
                loading={creatingSignupReqs}
                disabled={!selectedTagGroupIds.length}
                onClick={handleSaveTagGroups}
            />
        </div>
    );

    const deleteTagGroupFooter = () => (
        <div className="flex justify-content-end">
            <Button
                label={t('common.no')}
                icon="pi pi-times"
                className="p-button-text"
                onClick={() => setReqToRemove(null)}
                disabled={deletingSignupReq}
            />
            <Button
                label={t('common.ok')}
                icon="pi pi-check"
                loading={deletingSignupReq}
                onClick={handleConfirmRemoveTagGroup}
            />
        </div>
    );

    return (
        <>
            <CustomToast
                {...toastData}
                trigger={toastTrigger}
                handleActionsAfterToast={() => setToastTrigger(false)}
                handleActionsAfterErrorToast={() => setToastTrigger(false)}
            />

            <ConfirmDialog
                visible={showConfirm}
                onHide={() => {
                    setShowConfirm(false);
                    setReqsEnabled(business?.reqsEnabled ?? false);
                }}
                header={reqsEnabled
                    ? t('signupRequirements.disable_header')
                    : t('signupRequirements.enable_header')}
                footer={confirmFooter()}
                style={{ width: '350px' }}
                message={reqsEnabled
                    ? t('signupRequirements.disable_confirmation')
                    : t('signupRequirements.enable_confirmation')}
            />

            <Dialog
                visible={showTagGroupDialog}
                onHide={handleTagGroupDialogClose}
                header={t('signupRequirements.select_tag_groups_header')}
                footer={tagGroupDialogFooter()}
                style={{ width: '640px' }}
            >
                <p className="text_line">{t('signupRequirements.select_tag_groups_description')}</p>
                {fetchingTagGroups ? (
                    <p>{t('signupRequirements.loading_tag_groups')}</p>
                ) : !availableTagGroups.length ? (
                    <p>{awaitingTagGroupSelection
                        ? t('signupRequirements.no_tag_groups')
                        : t('signupRequirements.no_tag_groups_to_add')}</p>
                ) : (
                    <div className="mt-3">
                        {availableTagGroups.map((group) => (
                            <div key={group.id} className="flex align-items-center mb-2">
                                <Checkbox
                                    inputId={`signup-req-tag-group-${group.id}`}
                                    checked={selectedTagGroupIds.some(
                                        (groupId) => String(groupId) === String(group.id)
                                    )}
                                    onChange={(e) => handleTagGroupToggle(group.id, e.checked)}
                                />
                                <label
                                    htmlFor={`signup-req-tag-group-${group.id}`}
                                    className="ml-2 mb-0 cursor-pointer"
                                >
                                    {group.name}
                                </label>
                            </div>
                        ))}
                    </div>
                )}
                {saveAttempted && !selectedTagGroupIds.length && (
                    <p className="mt-2 text-red-500">{t('signupRequirements.tag_groups_required')}</p>
                )}
            </Dialog>

            <ConfirmDialog
                visible={!!reqToRemove}
                onHide={() => setReqToRemove(null)}
                header={t('signupRequirements.tag_groups_delete_header')}
                footer={deleteTagGroupFooter()}
                style={{ width: '350px' }}
                message={t('signupRequirements.tag_groups_delete_confirmation')}
            />

            <hr className="my-4" />

            <div className="grid">
                <section className="col-3">
                    <p className="font-bold text_line">{t('signupRequirements.title')}</p>
                </section>

                <section className="col-6">
                    <p className="text_line">{t('signupRequirements.description')}</p>
                    {reqsEnabled && (
                        <div className="mt-2">
                            <div className="flex align-items-center justify-content-between gap-2">
                                <p className="font-bold text_line m-0">{t('signupRequirements.tag_groups_title')}</p>
                                <Button
                                    label={t('signupRequirements.add_tag_groups')}
                                    icon="pi pi-plus"
                                    className="p-button-text p-button-sm"
                                    onClick={handleOpenAddTagGroups}
                                    disabled={creatingSignupReqs || deletingSignupReq || togglingReqsEnabled}
                                />
                            </div>
                            {fetchingSignupReqs ? (
                                <span className="text-sm">{t('signupRequirements.loading_requirements')}</span>
                            ) : signupRequirementNames.length ? (
                                <ul className="m-0 pl-3">
                                    {signupRequirementNames.map((req) => (
                                        <li key={req.id ?? req.name} className="text-sm flex align-items-center gap-2 mb-1">
                                            <span>{req.name}</span>
                                            <Button
                                                icon="pi pi-times"
                                                className="p-button-text p-button-rounded p-button-sm"
                                                aria-label={t('signupRequirements.tag_groups_delete_header')}
                                                onClick={() => handleRequestRemoveTagGroup(req)}
                                                disabled={deletingSignupReq}
                                            />
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <span className="text-sm">{t('signupRequirements.no_requirements')}</span>
                            )}
                            <SignupReqExclusionsSection
                                businessId={businessId}
                                showToast={showToast}
                            />
                        </div>
                    )}
                </section>

                <section className="col-3 flex justify-content-end gap-2">
                    <InputSwitch
                        checked={reqsEnabled}
                        onChange={() => setShowConfirm(true)}
                        disabled={togglingReqsEnabled || creatingSignupReqs || deletingSignupReq || showTagGroupDialog}
                    />
                    <p className="font-bold text_line">
                        {reqsEnabled ? t('api_settings.on') : t('api_settings.off')}
                    </p>
                </section>
            </div>
        </>
    );
};
